/**
 * Lazy Loading Translation System for Retroact
 *
 * Translations are split into domains and loaded on-demand:
 * - common: Loaded immediately (critical UI elements)
 * - Other domains: Loaded dynamically when needed
 *
 * ── Why this file has no locale list in it ─────────────────────────────────
 * It used to import en/fr common.json by name and repeat the pair in `messages`
 * and `loadedDomains`. Three places to edit per locale, and nothing failed when
 * one was missed — the locale simply rendered English with no error. Adding the
 * five European locales would have meant fifteen edits.
 *
 * `import.meta.glob(..., { eager: true })` makes the filesystem the source of
 * truth instead: a new `src/locales/<code>/` directory with a common.json is
 * picked up by the bundler with no edit here at all. The lazy domains below
 * already resolved by template literal, so they never needed a list.
 */

// Eagerly load every locale's `common` domain (critical for first render).
// Vite resolves this glob at build time — it is not a runtime directory read.
const commonModules = import.meta.glob('./locales/*/common.json', { eager: true })

/** Locale code -> its `common` messages, derived from the glob keys. */
const messages = {}
for (const [path, mod] of Object.entries(commonModules)) {
  const code = path.match(/\.\/locales\/([^/]+)\/common\.json$/)?.[1]
  if (code) messages[code] = { ...(mod.default ?? mod) }
}

/** Every locale the app ships, in a stable order. */
export const AVAILABLE_LOCALES = Object.keys(messages).sort()

// Define available translation domains
const DOMAINS = [
  'common',   // Loaded immediately (above)
  'emails',   // Lazy loaded
  'auth',     // Lazy loaded
  'reports',  // Lazy loaded
  'validation', // Lazy loaded
  'navigation', // Lazy loaded
  'tickets'   // Lazy loaded
]

// Cache for loaded domains — every discovered locale starts with `common`.
const loadedDomains = {}
for (const code of AVAILABLE_LOCALES) loadedDomains[code] = new Set(['common'])

/**
 * Dynamically import a translation domain
 * @param {string} locale - Language code (en, fr, fr-fr, es, it, nl, pl)
 * @param {string} domain - Domain name (emails, auth, reports, etc.)
 * @returns {Promise<Object>} Translation object
 */
export async function loadDomain(locale, domain) {
  // Skip if already loaded
  if (loadedDomains[locale]?.has(domain)) {
    return messages[locale]
  }

  try {
    // Dynamic import based on locale and domain
    const translations = await import(`./locales/${locale}/${domain}.json`)

    // Merge into existing messages
    messages[locale] = {
      ...messages[locale],
      ...translations.default
    }

    // Mark as loaded
    if (!loadedDomains[locale]) {
      loadedDomains[locale] = new Set()
    }
    loadedDomains[locale].add(domain)

    return messages[locale]
  } catch (error) {
    console.error(`Failed to load ${locale}/${domain} translations:`, error)
    return messages[locale]
  }
}

/**
 * Load multiple domains at once
 * @param {string} locale - Language code
 * @param {string[]} domains - Array of domain names
 * @returns {Promise<Object>} Merged translations
 */
export async function loadDomains(locale, domains) {
  await Promise.all(domains.map(domain => loadDomain(locale, domain)))
  return messages[locale]
}

/**
 * Preload all remaining domains for a locale
 * Call this after initial render to load remaining translations in background
 * @param {string} locale - Language code
 */
export async function preloadAllDomains(locale) {
  const unloadedDomains = DOMAINS.filter(d => !loadedDomains[locale]?.has(d))
  if (unloadedDomains.length > 0) {
    await loadDomains(locale, unloadedDomains)
  }
}

/**
 * Get current messages (use this with vue-i18n)
 */
export function getMessages() {
  return messages
}

// Export initial messages for vue-i18n initialization
export default messages
